/**
 * Weather Cache
 * Caches weather forecasts to reduce API calls and improve performance
 * 
 * Cache Strategy:
 * - Current weather: 30 minutes
 * - Forecast data: 1 hour
 * - Geocoding results: 24 hours (locations don't change)
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { WeatherForecast } from '@/services/weather';

const WEATHER_CACHE_KEY = '@tripflow_weather_forecasts';
const GEOCODE_CACHE_KEY = '@tripflow_geocode_results';

// Cache expiry times
const CURRENT_WEATHER_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
const FORECAST_EXPIRY_MS = 60 * 60 * 1000; // 1 hour
const GEOCODE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachedWeatherForecast = {
  data: WeatherForecast;
  cachedAt: number;
  requestKey: string;
};

type WeatherCache = {
  [key: string]: CachedWeatherForecast;
};

type CachedGeocode = {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  cachedAt: number;
};

type GeocodeCache = {
  [key: string]: CachedGeocode;
};

/**
 * Generate cache key for weather forecast request.
 * Uses normalized params so different callers with equivalent requests hit the same cache.
 */
const generateWeatherCacheKey = (
  latitude: number,
  longitude: number,
  targetDate?: string,
  days?: number
): string => {
  const lat = latitude.toFixed(4);
  const lon = longitude.toFixed(4);
  // Normalize targetDate to YYYY-MM-DD so "2025-02-15" and "2025-02-15T00:00:00Z" match
  const date = targetDate && targetDate.length >= 10 ? targetDate.slice(0, 10) : (targetDate || 'current');
  const dayCount = days ?? 5;
  return `weather|${lat}|${lon}|${date}|${dayCount}`;
};

/**
 * Normalize weather request params for cache key consistency across call sites.
 * Use these values when calling getCachedWeatherForecast and cacheWeatherForecast.
 */
export const normalizeWeatherCacheParams = (
  latitude: number,
  longitude: number,
  targetDate?: string,
  days?: number
): { lat: number; lon: number; targetDate: string | undefined; days: number } => {
  const lat = Number(Number(latitude).toFixed(4));
  const lon = Number(Number(longitude).toFixed(4));
  const target = targetDate && targetDate.length >= 10 ? targetDate.slice(0, 10) : targetDate;
  const dayCount = days ?? 5;
  return { lat, lon, targetDate: target, days: dayCount };
};

/**
 * Generate cache key for geocoding
 */
const generateGeocodeCacheKey = (destination: string): string => {
  return `geocode|${destination.toLowerCase().trim()}`;
};

/**
 * Get cached weather forecast
 */
export const getCachedWeatherForecast = async (
  latitude: number,
  longitude: number,
  targetDate?: string,
  days?: number
): Promise<WeatherForecast | null> => {
  try {
    const cacheData = await AsyncStorage.getItem(WEATHER_CACHE_KEY);
    if (!cacheData) return null;

    const cache: WeatherCache = JSON.parse(cacheData);
    const key = generateWeatherCacheKey(latitude, longitude, targetDate, days);
    const cached = cache[key];

    if (!cached) return null;

    // Determine expiry based on whether it's current weather or forecast
    const isCurrentWeather = !targetDate || new Date(targetDate) <= new Date();
    const expiryMs = isCurrentWeather ? CURRENT_WEATHER_EXPIRY_MS : FORECAST_EXPIRY_MS;

    // Check if cache is expired
    const age = Date.now() - cached.cachedAt;
    if (age > expiryMs) {
      // Remove expired entry
      delete cache[key];
      await AsyncStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return cached.data;
  } catch (error) {
    console.warn('[WeatherCache] Error getting cached weather:', error);
    return null;
  }
};

/**
 * Cache weather forecast
 */
export const cacheWeatherForecast = async (
  latitude: number,
  longitude: number,
  forecast: WeatherForecast,
  targetDate?: string,
  days?: number
): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(WEATHER_CACHE_KEY);
    const cache: WeatherCache = cacheData ? JSON.parse(cacheData) : {};

    const key = generateWeatherCacheKey(latitude, longitude, targetDate, days);
    cache[key] = {
      data: forecast,
      cachedAt: Date.now(),
      requestKey: key,
    };

    // Limit cache size to 500 entries
    const entries = Object.keys(cache);
    if (entries.length > 500) {
      // Remove oldest 100 entries
      const sorted = entries.sort((a, b) => cache[a].cachedAt - cache[b].cachedAt);
      sorted.slice(0, 100).forEach(k => delete cache[k]);
    }

    await AsyncStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[WeatherCache] Error caching weather:', error);
  }
};

/**
 * Get cached geocode result
 */
export const getCachedGeocode = async (
  destination: string
): Promise<{ latitude: number; longitude: number; city: string; country: string } | null> => {
  try {
    const cacheData = await AsyncStorage.getItem(GEOCODE_CACHE_KEY);
    if (!cacheData) return null;

    const cache: GeocodeCache = JSON.parse(cacheData);
    const key = generateGeocodeCacheKey(destination);
    const cached = cache[key];

    if (!cached) return null;

    // Check if cache is expired
    const age = Date.now() - cached.cachedAt;
    if (age > GEOCODE_EXPIRY_MS) {
      // Remove expired entry
      delete cache[key];
      await AsyncStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return {
      latitude: cached.latitude,
      longitude: cached.longitude,
      city: cached.city,
      country: cached.country,
    };
  } catch (error) {
    console.warn('[WeatherCache] Error getting cached geocode:', error);
    return null;
  }
};

/**
 * Cache geocode result
 */
export const cacheGeocode = async (
  destination: string,
  latitude: number,
  longitude: number,
  city: string,
  country: string
): Promise<void> => {
  try {
    const cacheData = await AsyncStorage.getItem(GEOCODE_CACHE_KEY);
    const cache: GeocodeCache = cacheData ? JSON.parse(cacheData) : {};

    const key = generateGeocodeCacheKey(destination);
    cache[key] = {
      latitude,
      longitude,
      city,
      country,
      cachedAt: Date.now(),
    };

    // Limit cache size to 1000 entries (geocoding is more stable)
    const entries = Object.keys(cache);
    if (entries.length > 1000) {
      // Remove oldest 200 entries
      const sorted = entries.sort((a, b) => cache[a].cachedAt - cache[b].cachedAt);
      sorted.slice(0, 200).forEach(k => delete cache[k]);
    }

    await AsyncStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[WeatherCache] Error caching geocode:', error);
  }
};

/**
 * Clear weather cache
 */
export const clearWeatherCache = async (): Promise<void> => {
  try {
    await AsyncStorage.removeItem(WEATHER_CACHE_KEY);
    await AsyncStorage.removeItem(GEOCODE_CACHE_KEY);
  } catch (error) {
    console.warn('[WeatherCache] Error clearing cache:', error);
  }
};

/**
 * Clear expired entries from cache
 */
export const cleanupExpiredCache = async (): Promise<void> => {
  try {
    // Clean weather cache
    const weatherData = await AsyncStorage.getItem(WEATHER_CACHE_KEY);
    if (weatherData) {
      const cache: WeatherCache = JSON.parse(weatherData);
      const now = Date.now();
      let hasChanges = false;

      for (const [key, cached] of Object.entries(cache)) {
        const isCurrentWeather = !cached.data.forecast || cached.data.forecast.length === 0;
        const expiryMs = isCurrentWeather ? CURRENT_WEATHER_EXPIRY_MS : FORECAST_EXPIRY_MS;
        
        if (now - cached.cachedAt > expiryMs) {
          delete cache[key];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        await AsyncStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cache));
      }
    }

    // Clean geocode cache
    const geocodeData = await AsyncStorage.getItem(GEOCODE_CACHE_KEY);
    if (geocodeData) {
      const cache: GeocodeCache = JSON.parse(geocodeData);
      const now = Date.now();
      let hasChanges = false;

      for (const [key, cached] of Object.entries(cache)) {
        if (now - cached.cachedAt > GEOCODE_EXPIRY_MS) {
          delete cache[key];
          hasChanges = true;
        }
      }

      if (hasChanges) {
        await AsyncStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
      }
    }
  } catch (error) {
    console.warn('[WeatherCache] Error cleaning up expired cache:', error);
  }
};


